import { filterClasses } from "@swivl-mono/helpers";
import { useEffect, useMemo, useState } from "react";
import { TimeHelper } from "../../Helpers/Time/Time.Helper";

type Outlets = "wrapper" | "svg" | "progressCircle" | "text"

interface BaseCircularCountdownProps {
  classNames?: { [Key in Outlets]?: string };
  size?: number;
  startTime: number; // in milliseconds
  strokeWidth?: number;
  timeUntilZero: number; // in milliseconds
}

export function CircularCountdown({
  classNames = { progressCircle: "stroke-orange-500", text: "text-black" },
  startTime,
  timeUntilZero,
  size = 32,
  strokeWidth = 2
}: BaseCircularCountdownProps) {


  const serverTime = TimeHelper.getSyncedDate().getTime() // Ensure time is synced before starting countdown.
  const initialRemainingTime = Math.max(timeUntilZero - serverTime, 0);
  const [remainingTime, setRemainingTime] = useState(initialRemainingTime);

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const duration = useMemo(() => timeUntilZero - startTime, [timeUntilZero, startTime]);

  // update remaining time
  useEffect(() => {
    const tick = () => {
      const serverTime = TimeHelper.getSyncedDate().getTime();
      const timeLeft = timeUntilZero - serverTime;
      setRemainingTime(Math.max(0, timeLeft));
    }
    tick();
    const interval = setInterval(tick, 200);

    return () => clearInterval(interval);
  }, [timeUntilZero]);

  const progress = (remainingTime / duration) * circumference;
  const strokeDashoffset = progress + circumference;

  return (
    <div className={filterClasses("flex items-center justify-center h-full", classNames.wrapper)}>
      <svg
        className={filterClasses(`size-${size} text-gray-200`, classNames.svg)}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          stroke="#e0e0e0"
          fill="transparent"
        />
        {/* progress circle */}
        <circle
          className={filterClasses("fill-transparent", classNames.progressCircle)}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{
            transition: 'stroke-dashoffset 1s linear',
          }}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />

        <text
          className={filterClasses("font-semibold", classNames.text)}
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="12"
        >
          {Math.ceil(remainingTime / 1000)}s
        </text>
      </svg>
    </div>
  );
}


